/**
 * Bulk Draw Backfill — admin tool for posting MANY past draws at once
 * (used when the upstream RapidAPI lottery feed is down, or for catching
 * up after a multi-day outage). Each row validates digit count locally
 * before the cashier can submit, so a 3-digit Pick 4 mistake (which would
 * silently no-op the API or worse) is impossible to commit.
 *
 * Flow:
 *  1. Cashier picks a starting date.
 *  2. The form pre-builds rows for every (state × pick_type × drawing_time)
 *     combo that is MISSING in the DB for that date.
 *  3. Cashier pastes/types numbers from the official state lottery site.
 *  4. Local validation per row (state, pick_type, draw_date, drawing_time,
 *     digit count matches pick_type, all digits 0-9).
 *  5. Submit posts each valid row to /api/lottery/admin/draw in parallel.
 *  6. Per-row status displayed ✓ posted, ✗ failed (with reason), or ⏭ skipped.
 *
 * NB: posting auto-settles tickets server-side. The UI surfaces the API
 * response settlement counts in a final toast so the cashier knows exactly
 * how many winners/losers were processed.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Database, ArrowRight, ImageIcon, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const STATES = ['TN', 'IL', 'MS'];
const PICK_TYPES = [3, 4];
const DRAWING_TIMES = ['midday', 'evening'];

/** Parse user input "1,2,3,4" or "1 2 3 4" or "1-2-3-4" → [1,2,3,4]. */
const parseNums = (raw) => {
    if (!raw) return [];
    return String(raw)
        .replace(/[,\-\s]+/g, '')
        .split('')
        .filter((c) => /[0-9]/.test(c))
        .map((c) => parseInt(c, 10));
};

const todayISO = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};
const yesterdayISO = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const buildSkeletonRows = (date) => {
    const rows = [];
    for (const state of STATES) {
        for (const pt of PICK_TYPES) {
            for (const dt of DRAWING_TIMES) {
                rows.push({
                    key: `${date}|${state}|${pt}|${dt}`,
                    draw_date: date,
                    state,
                    pick_type: pt,
                    drawing_time: dt,
                    numbersRaw: '',
                    status: 'pending', // pending | posted | failed | skipped | exists
                    message: '',
                });
            }
        }
    }
    return rows;
};

export const BulkDrawBackfillPanel = () => {
    // We track a SET of active dates so a single screenshot covering many
    // days (e.g. tnlottery.com's results history page) auto-builds rows
    // for every date the OCR detected — not just the picker's current
    // date. The picker still lets the user prime an empty date manually.
    const [activeDates, setActiveDates] = useState(() => new Set([yesterdayISO()]));
    const [drawDate, setDrawDate] = useState(yesterdayISO());
    const [rows, setRows] = useState(() => buildSkeletonRows(yesterdayISO()));
    const [submitting, setSubmitting] = useState(false);
    const [existingKeys, setExistingKeys] = useState(new Map());
    const [parsing, setParsing] = useState(false);
    const [ocrReviewPending, setOcrReviewPending] = useState(false);
    const [lastOcrCount, setLastOcrCount] = useState(0);
    const [ocrUploadsCount, setOcrUploadsCount] = useState(0);
    const [totalOcrDraws, setTotalOcrDraws] = useState(0);
    const [batchProgress, setBatchProgress] = useState(null); // {done,total} while a multi-upload batch is OCR'ing
    // Combos with pending tickets but no posted draw yet. Surfaces the exact
    // (state, pick_type, draw_date, drawing_time) tuples the cashier must
    // post numbers for — auto-refreshes after every Post All.
    const [pendingCombos, setPendingCombos] = useState([]);
    const [loadingPending, setLoadingPending] = useState(false);
    const fileInputRef = useRef(null);

    const refreshPendingCombos = useCallback(async () => {
        setLoadingPending(true);
        try {
            const { data } = await axios.get(`${API}/lottery/admin/pending-without-draw`, {
                withCredentials: true,
            });
            setPendingCombos(Array.isArray(data?.combos) ? data.combos : []);
        } catch {
            // Silent — admin will still see the panel without the alert
        } finally {
            setLoadingPending(false);
        }
    }, []);

    useEffect(() => {
        refreshPendingCombos();
    }, [refreshPendingCombos]);

    const addPendingCombo = useCallback((combo) => {
        // Add the combo's date to active dates AND pre-focus its row.
        setActiveDates((prev) => {
            if (prev.has(combo.draw_date)) return prev;
            const next = new Set(prev);
            next.add(combo.draw_date);
            return next;
        });
        // Defer to let the skeleton rebuild
        setTimeout(() => {
            const key = `${combo.draw_date}|${combo.state}|${combo.pick_type}|${combo.drawing_time}`;
            const el = document.querySelector(`[data-row-key="${key}"]`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const input = el.querySelector('input[type="text"], input:not([type])');
                if (input) input.focus();
            }
        }, 120);
    }, []);

    // Whenever the active-dates set changes, rebuild the skeleton to cover
    // EVERY date in the set — but preserve any in-progress input the user
    // has already typed for existing rows.
    useEffect(() => {
        setRows((prev) => {
            const prevByKey = new Map(prev.map((r) => [r.key, r]));
            const next = [];
            for (const date of activeDates) {
                for (const r of buildSkeletonRows(date)) {
                    next.push(prevByKey.get(r.key) || r);
                }
            }
            // Sort by date DESC (newest first) so the freshest draws are
            // always at the top of the table.
            next.sort((a, b) => {
                if (a.draw_date !== b.draw_date) return b.draw_date.localeCompare(a.draw_date);
                if (a.state !== b.state) return a.state.localeCompare(b.state);
                if (a.pick_type !== b.pick_type) return a.pick_type - b.pick_type;
                return a.drawing_time.localeCompare(b.drawing_time);
            });
            return next;
        });
    }, [activeDates]);

    const mergeOcrDraws = useCallback((draws) => {
        // First, add every OCR'd date to the active set so the skeleton
        // includes them. Then populate the matching cells.
        setActiveDates((prev) => {
            const next = new Set(prev);
            for (const d of draws) {
                if (d.draw_date) next.add(d.draw_date);
            }
            return next;
        });
        // Defer the populate by a tick so the skeleton-rebuild useEffect
        // has run with the new dates included.
        setTimeout(() => {
            setRows((prev) => {
                const byKey = new Map(prev.map((r) => [r.key, r]));
                for (const d of draws) {
                    if (!d.draw_date) continue;
                    const key = `${d.draw_date}|${d.state}|${d.pick_type}|${d.drawing_time}`;
                    const existing = byKey.get(key);
                    if (!existing) continue;
                    byKey.set(key, {
                        ...existing,
                        numbersRaw: d.winning_numbers.join('-'),
                        status: 'pending',
                        message: '',
                        fromOcr: true,
                        ocrConfidence: d.confidence || 'medium',
                    });
                }
                return Array.from(byKey.values());
            });
        }, 60);
    }, []);

    const removeDate = useCallback((date) => {
        setActiveDates((prev) => {
            if (prev.size <= 1) {
                // Keep at least one active date in the picker
                return prev;
            }
            const next = new Set(prev);
            next.delete(date);
            return next;
        });
    }, []);

    // Core: OCR one image, merge its draws, bump counters. No toast / no
    // `parsing` side-effects so it can be reused by BOTH the single-file
    // (paste) path and the multi-file batch path. Returns a small result.
    const ingestFile = useCallback(async (file) => {
        if (!file) return { ok: false, error: 'empty' };
        if (!file.type?.startsWith('image/')) return { ok: false, error: 'not-image' };
        if (file.size > 8 * 1024 * 1024) return { ok: false, error: 'too-big' };
        try {
            const fd = new FormData();
            fd.append('image', file);
            const { data } = await axios.post(`${API}/lottery/admin/parse-screenshot`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const draws = data?.draws || [];
            if (draws.length === 0) return { ok: false, error: data?.warning || 'no-draws' };
            mergeOcrDraws(draws);
            const uniqueDates = [...new Set(draws.map((d) => d.draw_date).filter(Boolean))];
            setOcrReviewPending(true);
            setLastOcrCount(draws.length);
            setOcrUploadsCount((c) => c + 1);
            setTotalOcrDraws((t) => t + draws.length);
            return { ok: true, count: draws.length, dates: uniqueDates };
        } catch (e) {
            const detail = e.response?.data?.detail || e.message || 'OCR failed';
            return { ok: false, error: typeof detail === 'string' ? detail : 'OCR failed' };
        }
    }, [mergeOcrDraws]);

    // Single screenshot (used by paste-from-clipboard).
    const handleScreenshot = useCallback(async (file) => {
        if (!file) return;
        if (!file.type?.startsWith('image/')) { toast.error('Please upload an image file (PNG / JPG)'); return; }
        if (file.size > 8 * 1024 * 1024) { toast.error('Image too large (max 8 MB)'); return; }
        setParsing(true);
        const r = await ingestFile(file);
        setParsing(false);
        if (!r.ok) {
            toast.error(
                r.error === 'no-draws'
                    ? 'No winning numbers found. This tool reads official RESULTS screenshots. To place a customer\'s ticket, use the "Place Ticket" tab instead.'
                    : (r.error || 'OCR failed'),
                { duration: 7000 }
            );
            return;
        }
        const dateLine = r.dates.length > 1 ? ` across ${r.dates.length} dates`
            : (r.dates.length === 1 ? ` for ${r.dates[0]}` : '');
        toast.success(`AI found ${r.count} drawing(s)${dateLine} — please spot-check before posting.`);
    }, [ingestFile]);

    // Up to MAX_OCR_BATCH screenshots at once (multi-select or multi-drop).
    // Processed sequentially to respect the OCR provider's rate limits; every
    // image's draws accumulate into the same table and one summary toast fires
    // at the end. Nothing is auto-posted — the cashier still clicks Post.
    const MAX_OCR_BATCH = 10;
    const handleScreenshots = useCallback(async (fileList) => {
        const files = Array.from(fileList || []).filter((f) => f && f.type?.startsWith('image/'));
        if (files.length === 0) { toast.error('Please choose image files (PNG / JPG)'); return; }
        if (files.length === 1) { handleScreenshot(files[0]); return; }
        const capped = files.slice(0, MAX_OCR_BATCH);
        const skipped = files.length - capped.length;
        setParsing(true);
        setBatchProgress({ done: 0, total: capped.length });
        let okFiles = 0, failFiles = 0, drawsLoaded = 0;
        for (let i = 0; i < capped.length; i++) {
            const r = await ingestFile(capped[i]);
            if (r.ok) { okFiles += 1; drawsLoaded += r.count; } else { failFiles += 1; }
            setBatchProgress({ done: i + 1, total: capped.length });
        }
        setBatchProgress(null);
        setParsing(false);
        const parts = [`${okFiles} screenshot${okFiles === 1 ? '' : 's'} read`, `${drawsLoaded} draw(s) loaded`];
        if (failFiles) parts.push(`${failFiles} unreadable`);
        if (skipped) parts.push(`${skipped} skipped (max ${MAX_OCR_BATCH})`);
        const msg = parts.join(' · ') + (drawsLoaded > 0 ? ' — spot-check before posting.' : '');
        if (drawsLoaded > 0) toast.success(msg); else toast.error(msg);
    }, [ingestFile, handleScreenshot]);

    // Paste-from-clipboard support — works anywhere on the document while the
    useEffect(() => {
        const handler = (e) => {
            if (!e.clipboardData) return;
            for (const item of e.clipboardData.items) {
                if (item.type?.startsWith('image/')) {
                    const file = item.getAsFile();
                    if (file) {
                        e.preventDefault();
                        handleScreenshot(file);
                        return;
                    }
                }
            }
        };
        window.addEventListener('paste', handler);
        return () => window.removeEventListener('paste', handler);
    }, [handleScreenshot]);

    const onDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = e.dataTransfer?.files;
        if (files && files.length) handleScreenshots(files);
    }, [handleScreenshots]);

    const onDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);

    // Whenever the user changes the date picker, ADD that date to the
    // active set (don't replace) and refresh the existing-keys index for
    // every active date so the cashier doesn't accidentally re-post.
    useEffect(() => {
        setActiveDates((prev) => {
            if (prev.has(drawDate)) return prev;
            const next = new Set(prev);
            next.add(drawDate);
            return next;
        });
    }, [drawDate]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const dates = Array.from(activeDates);
            const nextMap = new Map();
            // Parallel fetch — one per active date.
            await Promise.all(dates.map(async (date) => {
                try {
                    const { data } = await axios.get(`${API}/lottery/draws`, {
                        params: { date },
                        withCredentials: true,
                    });
                    const list = Array.isArray(data) ? data : (data?.draws || []);
                    list.forEach((d) => {
                        nextMap.set(
                            `${d.draw_date}|${d.state}|${d.pick_type}|${d.drawing_time}`,
                            Array.isArray(d.winning_numbers) ? d.winning_numbers : [],
                        );
                    });
                } catch { /* ignore — date filter blank or 4xx */ }
            }));
            if (!cancelled) setExistingKeys(nextMap);
        })();
        return () => { cancelled = true; };
    }, [activeDates]);

    // A row is a "mismatch" when a draw is already on file for that key
    // BUT the numbers the cashier just typed / OCR'd don't match. This
    // triggers the overwrite=true post path so the correct number wins.
    const getMismatch = useCallback((r) => {
        if (!(existingKeys instanceof Map)) return null;
        const onFile = existingKeys.get(r.key);
        if (!onFile || !Array.isArray(onFile) || onFile.length === 0) return null;
        const typed = parseNums(r.numbersRaw);
        if (typed.length !== r.pick_type) return null;
        if (typed.every((n, i) => n === onFile[i])) return null;
        return { onFile, typed };
    }, [existingKeys]);

    // Local validation summary — drives the Submit button's disabled state.
    const summary = useMemo(() => {
        const out = { valid: 0, invalid: 0, blank: 0, exists: 0, mismatch: 0 };
        for (const r of rows) {
            const mismatch = getMismatch(r);
            const onFile = existingKeys instanceof Map ? existingKeys.has(r.key) : false;
            const nums = parseNums(r.numbersRaw);
            if (mismatch) { out.mismatch++; continue; }
            if (onFile) { out.exists++; continue; }
            if (nums.length === 0) { out.blank++; continue; }
            if (nums.length !== r.pick_type) { out.invalid++; continue; }
            if (!nums.every((n) => n >= 0 && n <= 9)) { out.invalid++; continue; }
            out.valid++;
        }
        return out;
    }, [rows, existingKeys, getMismatch]);

    const updateRow = useCallback((key, patch) => {
        setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    }, []);

    const submitAll = useCallback(async () => {
        const toPost = rows.filter((r) => {
            const nums = parseNums(r.numbersRaw);
            if (nums.length !== r.pick_type) return false;
            if (!nums.every((n) => n >= 0 && n <= 9)) return false;
            const onFile = existingKeys.get?.(r.key);
            if (onFile && onFile.length && nums.every((n, i) => n === onFile[i])) {
                // identical to what's already on file → skip
                return false;
            }
            return true;
        });
        if (toPost.length === 0) {
            toast.error('Nothing valid to post yet — fill in at least one row');
            return;
        }
        const mismatchCount = toPost.filter((r) => {
            const onFile = existingKeys.get?.(r.key);
            return onFile && onFile.length && !parseNums(r.numbersRaw).every((n, i) => n === onFile[i]);
        }).length;
        const confirmMsg = mismatchCount > 0
            ? (
                `Post ${toPost.length} draw(s)? ${mismatchCount} of them will OVERWRITE a draw ` +
                `already on file (auto-reverting old settlements, then re-settling with the new number). ` +
                `Only proceed if you're sure the new number is correct.`
            )
            : (
                `Post ${toPost.length} draw(s)? This will instantly ` +
                `settle every pending ticket for those drawings — winners credited, ` +
                `losers marked. Cannot be undone without using the Correct Draw tool.`
            );
        if (!window.confirm(confirmMsg)) return;

        setSubmitting(true);
        let wins = 0;
        let fails = 0;
        let overwrites = 0;
        let totalCredited = 0;
        let totalSettled = 0;
        let totalClawback = 0;
        const results = await Promise.allSettled(toPost.map(async (r) => {
            const onFile = existingKeys.get?.(r.key);
            const isOverwrite = !!(onFile && onFile.length);
            try {
                const { data } = await axios.post(`${API}/lottery/admin/draw`, {
                    state: r.state,
                    pick_type: r.pick_type,
                    winning_numbers: parseNums(r.numbersRaw),
                    draw_date: r.draw_date,
                    drawing_time: r.drawing_time,
                    overwrite: isOverwrite,
                }, { withCredentials: true });
                const prefix = data?.overwrote ? 'overwrote · ' : '';
                const clawbackNote = data?.revert_summary
                    ? ` · clawed back $${(data.revert_summary.total_clawed_back || 0).toFixed(2)}`
                    : '';
                updateRow(r.key, {
                    status: 'posted',
                    message: `${prefix}settled ${data?.tickets_settled ?? 0} · paid $${(data?.total_payout ?? 0).toFixed(2)} · ${data?.winners ?? 0}W ${data?.losers ?? 0}L${clawbackNote}`,
                });
                wins++;
                if (data?.overwrote) {
                    overwrites++;
                    totalClawback += Number(data?.revert_summary?.total_clawed_back || 0);
                }
                totalCredited += Number(data?.total_payout || 0);
                totalSettled += Number(data?.tickets_settled || 0);
            } catch (e) {
                const detail = e.response?.data?.detail || e.message || 'failed';
                updateRow(r.key, { status: 'failed', message: String(detail) });
                fails++;
            }
        }));
        setSubmitting(false);

        if (wins > 0) {
            const overwriteNote = overwrites > 0
                ? ` · ${overwrites} overwrote (clawed back $${totalClawback.toFixed(2)})`
                : '';
            toast.success(
                `Backfilled ${wins} draw(s) — ${totalSettled} ticket(s) settled · $${totalCredited.toFixed(2)} paid out${overwriteNote}${fails > 0 ? ` · ${fails} failed` : ''}`,
            );
        } else {
            toast.error(`No draws posted — ${fails} failed`);
        }
        // Refresh existing-keys so the row badges flip to "exists" too
        setExistingKeys((prev) => {
            const next = new Map(prev);
            results.forEach((res, idx) => {
                if (res.status === 'fulfilled') {
                    next.set(toPost[idx].key, parseNums(toPost[idx].numbersRaw));
                }
            });
            return next;
        });
        // Auto-refresh the pending-without-draw alert — combos we just
        // posted for should disappear from the alert immediately.
        refreshPendingCombos();
    }, [rows, existingKeys, updateRow, refreshPendingCombos]);

    const handleClearRow = (key) => updateRow(key, { numbersRaw: '', status: 'pending', message: '' });

    return (
        <Card className="bg-zinc-900 border-amber-500/30" data-testid="bulk-draw-backfill-panel">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-300">
                    <Database className="w-5 h-5" />
                    Bulk Draw Backfill
                </CardTitle>
                <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-200 leading-snug" data-testid="draw-backfill-scope-note">
                    <strong>Official winning numbers only.</strong> Scanning a <strong>customer's bet ticket</strong> to place their bets? Use the <strong>“Place Ticket”</strong> tab instead — this uploader reads lottery <strong>results</strong>, not tickets.
                </div>
                <p className="text-[12px] text-zinc-400 leading-relaxed mt-2">
                    Drop a screenshot — even one that covers <strong>multiple days</strong> — and AI auto-fills every draw it finds across every date. Or add dates manually below. The form validates digit counts so a 3-digit Pick 4 can never sneak through. One click posts everything and auto-settles tickets.
                </p>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* ── Pending Tickets Awaiting Draws — red alert ───────────── */}
                {pendingCombos.length > 0 && (
                    <div
                        className="rounded-lg border-2 border-rose-500/60 bg-rose-500/10 p-3 space-y-2"
                        data-testid="bulk-pending-combos-alert"
                    >
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="w-5 h-5 text-rose-300 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <div className="text-[13px] font-black text-rose-200" data-testid="bulk-pending-combos-headline">
                                    {pendingCombos.reduce((s, c) => s + c.pending_count, 0)} ticket(s) waiting on {pendingCombos.length} missing draw{pendingCombos.length === 1 ? '' : 's'}
                                </div>
                                <div className="text-[11px] text-rose-200/80 leading-snug mt-0.5">
                                    These combos have pending tickets but no posted draw yet. Click one to jump to its row and enter the winning numbers.
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={refreshPendingCombos}
                                disabled={loadingPending}
                                className="text-[10px] text-rose-200/70 hover:text-white underline disabled:opacity-50"
                                data-testid="bulk-pending-combos-refresh"
                            >
                                {loadingPending ? 'refreshing…' : 'refresh'}
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {pendingCombos.map((c) => {
                                const key = `${c.draw_date}|${c.state}|${c.pick_type}|${c.drawing_time}`;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => addPendingCombo(c)}
                                        className="text-[11px] px-2 py-1 rounded border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/25 text-rose-100 font-mono transition-colors"
                                        data-testid={`bulk-pending-combo-${c.draw_date}-${c.state}-${c.pick_type}-${c.drawing_time}`}
                                        title={`Oldest ticket: ${c.oldest_ticket}`}
                                    >
                                        <span className="font-black">{c.state}</span> Pick{c.pick_type} {c.drawing_time} · <span className="text-rose-200/70">{c.draw_date}</span> · <span className="text-amber-300 font-black">{c.pending_count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="flex items-end gap-3 flex-wrap">
                    <div>
                        <label htmlFor="bulk-date" className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Add date</label>
                        <Input
                            id="bulk-date"
                            type="date"
                            value={drawDate}
                            onChange={(e) => setDrawDate(e.target.value)}
                            max={todayISO()}
                            className="bg-zinc-950 border-zinc-700 text-white w-44"
                            data-testid="bulk-draw-date"
                        />
                        <p className="text-[9px] text-zinc-500 mt-0.5">
                            {activeDates.size === 1 ? '1 date loaded' : `${activeDates.size} dates loaded`}
                        </p>
                    </div>
                    <div className="text-[11px] text-zinc-400 space-x-3 mb-1">
                        <span data-testid="bulk-summary-valid">✓ {summary.valid} valid</span>
                        <span data-testid="bulk-summary-blank">○ {summary.blank} empty</span>
                        <span className="text-rose-300" data-testid="bulk-summary-invalid">✗ {summary.invalid} invalid</span>
                        {summary.mismatch > 0 && (
                            <span className="text-amber-300 font-bold" data-testid="bulk-summary-mismatch" title="These rows have numbers different from the draw already on file. Posting will overwrite.">
                                ⚠ {summary.mismatch} will overwrite
                            </span>
                        )}
                        <span className="text-zinc-500" data-testid="bulk-summary-exists">⏭ {summary.exists} already on file</span>
                    </div>
                </div>

                {/* ── Screenshot OCR drop-zone ─────────────────────────────── */}
                <div
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    className="rounded-lg border-2 border-dashed border-indigo-500/40 bg-indigo-500/5 p-3 flex items-center gap-3 text-[12px]"
                    data-testid="bulk-ocr-dropzone"
                >
                    <Sparkles className="w-5 h-5 text-indigo-300 flex-shrink-0" />
                    <div className="flex-1 leading-snug text-zinc-300">
                        <span className="font-bold text-indigo-200">Drop screenshots</span> (or paste with Ctrl-V) — pick <strong>up to 10 at once</strong>, even ones showing <strong>multiple dates / states</strong>. AI reads them all into the table. You spot-check + click Post once at the end.
                        {batchProgress && (
                            <div className="mt-1 text-[11px] text-indigo-200 font-bold" data-testid="bulk-ocr-batch-progress">
                                Reading {batchProgress.done}/{batchProgress.total} screenshots…
                            </div>
                        )}
                        {ocrUploadsCount > 0 && (
                            <div className="mt-1 flex items-center gap-2 text-[11px]">
                                <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-bold"
                                    data-testid="bulk-ocr-uploads-count"
                                >
                                    ✓ {ocrUploadsCount} screenshot{ocrUploadsCount === 1 ? '' : 's'} processed · {totalOcrDraws} draw{totalOcrDraws === 1 ? '' : 's'} loaded
                                </span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!window.confirm('Clear everything and start over? This will wipe all OCR-filled rows.')) return;
                                        setActiveDates(new Set([yesterdayISO()]));
                                        setOcrUploadsCount(0);
                                        setTotalOcrDraws(0);
                                        setOcrReviewPending(false);
                                        setLastOcrCount(0);
                                    }}
                                    className="text-zinc-500 hover:text-rose-300 underline"
                                    data-testid="bulk-ocr-clear-all"
                                >
                                    clear all
                                </button>
                            </div>
                        )}
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        multiple
                        className="hidden"
                        onChange={(e) => { const fs = e.target.files; if (fs && fs.length) handleScreenshots(fs); e.target.value = ''; }}
                        data-testid="bulk-ocr-file-input"
                    />
                    <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={parsing}
                        className="bg-indigo-500 hover:bg-indigo-400 text-white font-bold"
                        data-testid="bulk-ocr-upload-btn"
                    >
                        {parsing
                            ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {batchProgress ? `Reading ${batchProgress.done}/${batchProgress.total}…` : 'Reading…'}</>
                            : <><ImageIcon className="w-4 h-4 mr-1" /> {ocrUploadsCount > 0 ? 'Add more' : 'Upload screenshots'}</>
                        }
                    </Button>
                </div>
                {ocrReviewPending && (
                    <div
                        className="rounded-md border border-amber-500/50 bg-amber-500/15 px-3 py-2 text-[12px] text-amber-200 flex items-start gap-2"
                        data-testid="bulk-ocr-review-banner"
                    >
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 leading-snug">
                            <strong>AI filled in {lastOcrCount} row(s).</strong> Please <em>spot-check every number</em> against the official site before clicking Post — OCR is not 100% accurate, especially on blurry / cropped images.
                            <button
                                type="button"
                                onClick={() => setOcrReviewPending(false)}
                                className="ml-2 underline text-amber-100 hover:text-white"
                                data-testid="bulk-ocr-review-dismiss"
                            >
                                I&apos;ve reviewed — dismiss
                            </button>
                        </div>
                    </div>
                )}

                <div className="overflow-x-auto rounded border border-zinc-800">
                    <table className="w-full text-sm">
                        <thead className="bg-zinc-950 text-[10px] uppercase tracking-wider text-zinc-500">
                            <tr>
                                <th className="text-left px-2 py-2">State</th>
                                <th className="text-left px-2 py-2">Game</th>
                                <th className="text-left px-2 py-2">Time</th>
                                <th className="text-left px-2 py-2">Winning numbers</th>
                                <th className="text-left px-2 py-2 w-72">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => {
                                const onFile = existingKeys.has(r.key);
                                const onFileNums = existingKeys.get?.(r.key);
                                const nums = parseNums(r.numbersRaw);
                                const wrongLen = nums.length > 0 && nums.length !== r.pick_type;
                                const isMismatch = onFile
                                    && Array.isArray(onFileNums) && onFileNums.length === r.pick_type
                                    && nums.length === r.pick_type
                                    && !nums.every((n, i) => n === onFileNums[i]);
                                // "already on file — skipped" only when there's no user input
                                // (or the input matches the on-file numbers). If the user typed
                                // different numbers we surface the mismatch UI instead and keep
                                // the input editable so they can correct or overwrite.
                                const skipAsExists = onFile && nums.length === 0;
                                const identicalToOnFile = onFile
                                    && Array.isArray(onFileNums) && onFileNums.length === r.pick_type
                                    && nums.length === r.pick_type
                                    && nums.every((n, i) => n === onFileNums[i]);
                                // Date-group header — renders before the first row of each new date.
                                const isGroupHead = idx === 0 || rows[idx - 1].draw_date !== r.draw_date;
                                return (
                                    <React.Fragment key={r.key}>
                                        {isGroupHead && (
                                            <tr className="bg-zinc-950/80">
                                                <td colSpan={5} className="px-2 py-1.5">
                                                    <div className="flex items-center justify-between">
                                                        <span
                                                            className="text-[11px] uppercase tracking-widest text-amber-300 font-black"
                                                            data-testid={`bulk-date-group-${r.draw_date}`}
                                                        >
                                                            {r.draw_date}
                                                        </span>
                                                        {activeDates.size > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeDate(r.draw_date)}
                                                                className="text-[10px] text-zinc-500 hover:text-rose-300 underline"
                                                                data-testid={`bulk-date-remove-${r.draw_date}`}
                                                            >
                                                                remove this date
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        <tr className={`border-t border-zinc-800 ${skipAsExists || identicalToOnFile ? 'opacity-60' : ''} ${isMismatch ? 'bg-rose-500/5' : ''}`} data-row-key={r.key}>
                                        <td className="px-2 py-1.5 font-bold text-zinc-200">{r.state}</td>
                                        <td className="px-2 py-1.5 text-zinc-300">Pick {r.pick_type}</td>
                                        <td className="px-2 py-1.5 text-zinc-400 capitalize">{r.drawing_time}</td>
                                        <td className="px-2 py-1.5">
                                            <Input
                                                value={r.numbersRaw}
                                                onChange={(e) => updateRow(r.key, { numbersRaw: e.target.value, status: 'pending', message: '', fromOcr: false })}
                                                disabled={r.status === 'posted' || submitting}
                                                placeholder={onFileNums?.length === r.pick_type ? onFileNums.join('-') : (r.pick_type === 3 ? '0-0-0' : '0-0-0-0')}
                                                maxLength={20}
                                                className={`bg-zinc-950 text-white font-mono w-32 ${wrongLen ? 'border-rose-500/60' : isMismatch ? 'border-amber-500/70 ring-1 ring-amber-500/40' : nums.length === r.pick_type ? 'border-emerald-500/60' : 'border-zinc-700'} ${r.fromOcr ? 'ring-1 ring-indigo-400/60' : ''}`}
                                                data-testid={`bulk-input-${r.draw_date}-${r.state}-${r.pick_type}-${r.drawing_time}`}
                                                inputMode="numeric"
                                                title={r.fromOcr ? `Auto-filled by AI (${r.ocrConfidence} confidence) — please spot-check` : (onFileNums?.length ? `On file: ${onFileNums.join('-')}. Type different numbers to overwrite.` : undefined)}
                                            />
                                        </td>
                                        <td className="px-2 py-1.5 text-[11px]">
                                            {isMismatch && r.status === 'pending' && (
                                                <span
                                                    className="text-amber-300 flex items-center gap-1"
                                                    data-testid={`bulk-status-${r.draw_date}-${r.state}-${r.pick_type}-${r.drawing_time}-mismatch`}
                                                    title={`Currently on file: ${onFileNums.join('-')}. Posting will overwrite it and re-settle all tickets.`}
                                                >
                                                    <AlertTriangle className="w-3 h-3" /> will overwrite {onFileNums.join('-')} → {nums.join('-')}
                                                </span>
                                            )}
                                            {(skipAsExists || identicalToOnFile) && !isMismatch && r.status === 'pending' && (
                                                <span className="text-zinc-500" data-testid={`bulk-status-${r.draw_date}-${r.state}-${r.pick_type}-${r.drawing_time}-exists`}>⏭ already on file{onFileNums?.length ? ` (${onFileNums.join('-')})` : ''} — skipped</span>
                                            )}
                                            {!onFile && r.status === 'pending' && wrongLen && (
                                                <span className="text-rose-300">need {r.pick_type} digits ({nums.length} so far)</span>
                                            )}
                                            {!onFile && r.status === 'pending' && !wrongLen && nums.length === r.pick_type && (
                                                <span className="text-emerald-300">ready — {nums.join('-')}</span>
                                            )}
                                            {r.status === 'posted' && (
                                                <span className="text-emerald-300 flex items-center gap-1" data-testid={`bulk-status-${r.draw_date}-${r.state}-${r.pick_type}-${r.drawing_time}-posted`}>
                                                    <CheckCircle2 className="w-3 h-3" /> {r.message}
                                                </span>
                                            )}
                                            {r.status === 'failed' && (
                                                <span className="text-rose-300 flex items-center gap-1" data-testid={`bulk-status-${r.draw_date}-${r.state}-${r.pick_type}-${r.drawing_time}-failed`}>
                                                    <XCircle className="w-3 h-3" /> {r.message}
                                                    <button type="button" onClick={() => handleClearRow(r.key)} className="ml-1 underline text-rose-300/70 hover:text-rose-200">clear</button>
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-between gap-3 pt-1">
                    <div className="text-[11px] text-amber-200/80 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Posting settles tickets immediately. Double-check official sites before submitting.
                    </div>
                    <Button
                        onClick={submitAll}
                        disabled={submitting || (summary.valid === 0 && summary.mismatch === 0)}
                        className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-black"
                        data-testid="bulk-submit-all"
                    >
                        {submitting
                            ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Posting…</>
                            : (summary.mismatch > 0
                                ? <>Post {summary.valid} new + overwrite {summary.mismatch} <ArrowRight className="w-4 h-4 ml-1" /></>
                                : <>Post {summary.valid} valid draw{summary.valid === 1 ? '' : 's'} <ArrowRight className="w-4 h-4 ml-1" /></>
                            )
                        }
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default BulkDrawBackfillPanel;
